import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Filter as FilterIcon, Building2, UserRound, X } from 'lucide-react';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import RecordChip from '../../../shared/RecordChip';
import RecordLookupField from '../../../shared/RecordLookupField';
import DateInput from '../../../shared/DateInput';
import { CategoryPill } from '../../../shared/Pill';
import { Pagination, PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { useStaffAuth } from '../../../shared/StaffAuthContext';
import { formatDate, todayInTenant } from '../../../shared/tenantClock';
import { ACTIVITY_TYPES, ACTIVITY_TYPE_LABELS, ACTIVITY_TYPE_ICONS, SYSTEM_ACTIVITY_TYPE } from '../Leads/activityConstants';
import LogActivityDrawer from '../Leads/LogActivityDrawer';
import MoveActivityDrawer from '../Leads/MoveActivityDrawer';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';

// CRM > Activities (issue #307, vis_activities.md §14) — every Activity from
// every record on one page, newest first, so nobody has to open each
// customer in turn. One row per Activity: its customer is the record it was
// logged against (activityController.listActivityLog). Opening a row uses the
// same edit panel and Move drawer as a customer's own timeline.

const FILTER_SCREEN_KEY = 'crm-activities';
const DEFAULT_FILTERS = {
  scope: 'everyone',
  activity_type: 'all',
  from: '',
  to: '',
  customer_id: '',
  customer_name: '',
  customer_type: '',
  task_status: 'all',
};
// The customer's name and type only travel with its id so a saved tab can
// show the picked customer again; the Filters badge counts the id.
const NOT_COUNTED = ['scope', 'customer_name', 'customer_type'];
// Open Tasks stands in for vis_activities.md §14's to-do queue until the
// §15 Task board is built.
const OPEN_TASKS_FILTERS = { activity_type: 'task', task_status: 'open' };
const SYSTEM_TABS = [{ key: 'open-tasks', label: 'Open Tasks', filters: OPEN_TASKS_FILTERS }];
const isOpenTasksView = (f) => f.activity_type === 'task' && f.task_status === 'open';
// Open and Overdue Tasks list earliest due first, like the Open Tasks tab.
const isDueOrderView = (f) => isOpenTasksView(f) || f.task_status === 'overdue';

const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
const TH_STYLE = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const TD_STYLE = { padding: '6px 8px', fontSize: 14 };
const SEARCH_DEBOUNCE_MS = 300;
const PORTAL_SOURCES = ['portal_download', 'portal_account', 'portal_order', 'portal_quote', 'portal_address'];

function staffLabel(a) {
  if (a.source === 'email_sync') return a.user?.name ? `${a.user.name} (Email Sync)` : 'Email Sync';
  if (PORTAL_SOURCES.includes(a.source)) return 'Customer portal';
  if (a.source === 'portal_logins') return 'Automatic';
  return a.user?.name || '—';
}

function ScopeSwitch({ scope, onChange }) {
  const btn = (key, label, title) => {
    const selected = scope === key;
    return (
      <button
        type="button"
        onClick={() => onChange(key)}
        title={title}
        style={{
          padding: '6px 12px',
          border: 'none',
          background: selected ? '#1976d2' : '#fff',
          color: selected ? '#fff' : '#333',
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{ display: 'inline-flex', border: '1px solid #bfc8d6', borderRadius: 6, overflow: 'hidden' }}>
      {btn('mine', 'Mine', 'Show Activities you logged and Tasks assigned to you')}
      {btn('everyone', 'Everyone', 'Show Activities logged by anyone')}
    </div>
  );
}

function ActivitiesPage() {
  const { canUpdate } = useScreenAccess();
  const { currentUser } = useStaffAuth();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  // A dashboard tile can open this list already filtered (initialFilters).
  const location = useLocation();
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...(location.state?.initialFilters || {}) }));
  // Free text, so an ad hoc box rather than a saved-tab field (same split
  // as Quotes' PO search).
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS)
    .filter((k) => !NOT_COUNTED.includes(k))
    .filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters({ ...DEFAULT_FILTERS, scope: filters.scope }); closeFilterPanel(); };

  // The edit panel and Move drawer, as on a customer's own timeline.
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [drawerActivityType, setDrawerActivityType] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [drawerEntity, setDrawerEntity] = useState(null);
  const [followUpFrom, setFollowUpFrom] = useState(null);
  const [movingActivity, setMovingActivity] = useState(null);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchText.trim()); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchText]);

  const dueOrderView = isDueOrderView(filters);
  // Ignores a slower earlier response landing after a newer one.
  const fetchSeq = useRef(0);
  const fetchRows = async ({ quiet = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!quiet) setLoading(true);
    try {
      const res = await axios.get('/api/activities/log', {
        params: {
          scope: filters.scope,
          activity_type: filters.activity_type !== 'all' ? filters.activity_type : undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
          commercial_entity_id: filters.customer_id || undefined,
          task_status: filters.task_status !== 'all' ? filters.task_status : undefined,
          q: search || undefined,
          sort: dueOrderView ? 'due' : undefined,
          page,
          limit,
        },
      });
      if (seq !== fetchSeq.current) return;
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    if (seq === fetchSeq.current) setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, search, page, limit]);

  const refresh = () => fetchRows({ quiet: true });

  const openActivity = async (a) => {
    try {
      const res = await axios.get(`/api/activities/${a.id}`);
      const full = res.data.data;
      setEditingActivity(full);
      setDrawerEntity(full.commercial_entity);
      setFollowUpFrom(null);
      setDrawerActivityType(full.activity_type);
      setLogDrawerOpen(true);
    } catch (err) {
      showError(err, 'Could not open the Activity');
    }
  };

  const openFollowUpTask = (activity) => {
    setEditingActivity(null);
    setDrawerEntity(activity.commercial_entity);
    setFollowUpFrom(activity);
    setDrawerActivityType('task');
    setLogDrawerOpen(true);
  };

  // Done drops a Task off a list showing open or overdue Tasks only, and Open
  // drops it off one showing done Tasks only; elsewhere it stays and its
  // status flips.
  const toggleDone = async (a, done) => {
    try {
      await axios.patch(`/api/activities/${a.id}/${done ? 'complete' : 'reopen'}`);
      const leaves = (['open', 'overdue'].includes(filters.task_status) && done) || (filters.task_status === 'done' && !done);
      if (leaves) {
        setRows((prev) => prev.filter((r) => r.id !== a.id));
        setPagination((prev) => ({ ...prev, total: Math.max(prev.total - 1, 0) }));
      } else {
        setRows((prev) => prev.map((r) => (r.id === a.id ? { ...r, is_done: done } : r)));
      }
      dismissError();
    } catch (err) {
      showError(err, 'Could not update Task status');
    }
  };

  const deleteActivity = async (a) => {
    if (!(await askConfirm('Delete this Activity?', { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/activities/${a.id}`);
      refresh();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const searchCustomers = (term) => axios
    .get('/api/commercial-entities/quick-search', { params: { q: term } })
    .then((res) => res.data.data || []);

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Whose</label><br />
        <select className="styled-input" value={draft.scope} onChange={(e) => setDraftField('scope', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="everyone">Everyone</option>
          <option value="mine">Mine</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Type</label><br />
        <select className="styled-input" value={draft.activity_type} onChange={(e) => setDraftField('activity_type', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All types</option>
          {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Date</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DateInput className="styled-input" value={draft.from || ''} onChange={(e) => setDraftField('from', e.target.value)} style={{ flex: 1 }} />
          <span style={{ fontSize: 13, color: '#5a6b85' }}>to</span>
          <DateInput className="styled-input" value={draft.to || ''} onChange={(e) => setDraftField('to', e.target.value)} style={{ flex: 1 }} />
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Customer</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RecordLookupField
            value={draft.customer_id ? { id: draft.customer_id, label: draft.customer_name } : null}
            search={searchCustomers}
            onSelect={(r) => {
              setDraftField('customer_id', String(r.id));
              setDraftField('customer_name', r.name);
              setDraftField('customer_type', r.type || '');
            }}
            href={draft.customer_id ? `/commercial-entities/${draft.customer_id}` : undefined}
            icon={draft.customer_type === 'individual' ? UserRound : Building2}
            placeholder="Search name..."
            openTitle="Open customer"
            changeTitle="Change customer"
            width="100%"
          />
          {draft.customer_id && (
            <button
              type="button"
              title="Clear customer"
              onClick={() => { setDraftField('customer_id', ''); setDraftField('customer_name', ''); setDraftField('customer_type', ''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5a6b85', padding: 4 }}
            >
              <X size={15} />
            </button>
          )}
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Task status</label><br />
        <select className="styled-input" value={draft.task_status} onChange={(e) => setDraftField('task_status', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All</option>
          <option value="open">Open Tasks</option>
          <option value="overdue">Overdue Tasks</option>
          <option value="done">Completed Tasks</option>
        </select>
      </div>
    </>
  );

  const today = todayInTenant();
  const totalCols = 7;

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Activities</h2>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Search topic or customer..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 240 }}
          />
          {searchText && (
            <button type="button" onClick={() => setSearchText('')} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        {currentUser && <ScopeSwitch scope={filters.scope} onChange={(scope) => applyFilters({ ...filters, scope })} />}
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={(draft, setDraftField) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{renderDrawerFields(draft, setDraftField)}</div>
          )}
          systemTabs={SYSTEM_TABS}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Activities list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      <div style={{ margin: '18px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '13%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '6%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={TH_STYLE}>{dueOrderView ? 'Due' : 'Date'}</th>
              <th style={TH_STYLE}>Type</th>
              <th style={TH_STYLE}>Topic</th>
              <th style={TH_STYLE}>Customer</th>
              <th style={TH_STYLE}>Staff</th>
              <th style={TH_STYLE}>Outcome</th>
              <th style={{ ...TH_STYLE, textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((a) => {
              const Icon = ACTIVITY_TYPE_ICONS[a.activity_type];
              const isTask = a.activity_type === 'task';
              const isSystem = a.activity_type === SYSTEM_ACTIVITY_TYPE;
              const overdue = isTask && !a.is_done && a.activity_date && a.activity_date < today;
              const owner = a.commercial_entity;
              return (
                <tr
                  key={a.id}
                  className="list-row"
                  onClick={isSystem ? undefined : () => openActivity(a)}
                  style={{ cursor: isSystem ? 'default' : 'pointer', background: overdue ? '#fdf1f1' : undefined }}
                >
                  <td style={{ ...TD_STYLE, fontSize: 13, color: overdue ? '#b91c1c' : '#666', fontWeight: overdue ? 600 : 400 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isTask && (
                        <input
                          type="checkbox"
                          checked={!!a.is_done}
                          disabled={!canUpdate}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => toggleDone(a, e.target.checked)}
                          title={a.is_done ? 'Mark Open' : 'Mark Done'}
                          style={{ margin: 0, cursor: canUpdate ? 'pointer' : 'default' }}
                        />
                      )}
                      <span>{formatDate(a.activity_date) || '—'}</span>
                    </div>
                  </td>
                  <td style={{ ...TD_STYLE, fontSize: 13 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#5a6b85' }}>
                      {Icon && <Icon size={14} />}
                      {ACTIVITY_TYPE_LABELS[a.activity_type] || a.activity_type}
                    </span>
                  </td>
                  <td style={{ ...TD_STYLE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: a.is_done ? 'line-through' : 'none', color: a.is_done ? '#888' : undefined }} title={a.topic || ''}>
                    {a.topic || <span style={{ color: '#9aa5b5' }}>—</span>}
                  </td>
                  <td style={{ ...TD_STYLE, overflow: 'hidden' }}>
                    {owner && (
                      <RecordChip
                        size="sm"
                        label={owner.name}
                        href={`/commercial-entities/${owner.id}`}
                        icon={owner.type === 'individual' ? UserRound : Building2}
                        origin={{ label: 'Activities', path: '/crm-activities' }}
                        title={`Open ${owner.name}`}
                      />
                    )}
                  </td>
                  <td style={{ ...TD_STYLE, fontSize: 13, color: '#444' }}>
                    <div>{staffLabel(a)}</div>
                    {isTask && a.assignee && <div style={{ fontSize: 12, color: '#9aa5b5' }}>Assigned: {a.assignee.name}</div>}
                  </td>
                  <td style={{ ...TD_STYLE, fontSize: 13 }}>
                    {isTask ? (
                      overdue
                        ? <CategoryPill value="Overdue" color="#b91c1c" />
                        : <CategoryPill value={a.is_done ? 'Done' : 'Open'} color={a.is_done ? '#2e7d32' : '#1976d2'} />
                    ) : (a.outcome ? <CategoryPill value={a.outcome} /> : <span style={{ color: '#9aa5b5' }}>—</span>)}
                  </td>
                  <td style={{ ...TD_STYLE, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {!isSystem && (
                      <RowActionsMenu
                        ariaLabel="Activity actions"
                        items={[
                          { key: 'edit', label: 'Edit', onClick: () => openActivity(a) },
                          { key: 'follow-up', label: 'Create Task', needs: 'create', onClick: () => openFollowUpTask(a) },
                          { key: 'move', label: 'Move Activity', needs: 'update', onClick: () => setMovingActivity(a) },
                          { key: 'delete', label: 'Delete', danger: true, onClick: () => deleteActivity(a) },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Activities"
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />

      <LogActivityDrawer
        open={logDrawerOpen}
        onOpenChange={setLogDrawerOpen}
        activityType={drawerActivityType}
        entity={drawerEntity}
        contactOptions={[]}
        editingActivity={editingActivity}
        followUpFrom={followUpFrom}
        onSaved={refresh}
        onFollowUp={(saved) => openFollowUpTask(saved)}
      />
      <MoveActivityDrawer
        open={!!movingActivity}
        activity={movingActivity}
        onOpenChange={(o) => { if (!o) setMovingActivity(null); }}
        onMoved={refresh}
      />
    </div>
  );
}

export default ActivitiesPage;
